import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fab from '@mui/material/Fab';

const navItems = [
  { label: 'Services', href: '/services' },
  { label: 'Sectors', href: '/sectors' },
  { label: 'Projects', href: '/projects' },
  { label: 'Certifications', href: '/certifications' },
  { label: 'Why ANR', href: '/why-anr' },
  { label: 'Capabilities', href: '/capabilities' },
  { label: 'FAQ', href: '/faq' },
];

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleToggle = () => setOpen((prev) => !prev);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  const NavLinks = () => (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
      {navItems.map((item) => (
        <Button
          key={item.label}
          component="a"
          href={item.href}
          color="inherit"
          sx={{ 
            fontWeight: 500,
            px: 2,
            py: 1,
            borderRadius: 2,
            transition: 'all 0.3s ease',
            color: 'text.primary',
            textShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(26, 35, 126, 0.12)',
              transform: 'translateY(-1px)'
            }
          }}
          onClick={handleNavClick}
        >
          {item.label}
        </Button>
      ))}
      <Button
        variant="contained"
        color="secondary"
        component="a"
        href="tel:(516) 414-0165"
        sx={{ 
          ml: isMobile ? 0 : 1,
          px: 3,
          py: 1.5,
          borderRadius: 3,
          fontWeight: 600,
          boxShadow: '0 4px 14px 0 rgba(69, 90, 100, 0.3)',
          transition: 'all 0.3s ease',
          color: 'common.white',
          textShadow: '0 1px 2px rgba(0,0,0,0.35)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px 0 rgba(69, 90, 100, 0.4)'
          }
        }}
      >
        Call Now
      </Button>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0} 
        sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(11, 31, 59, 0.08)',
          transition: 'all 0.3s ease',
          zIndex: theme.zIndex.appBar
        }}
      >
        <Toolbar sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="a"
              href="/"
              aria-label="ANR Mechanical — Home"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                textDecoration: 'none',
                lineHeight: 0,
                transition: 'opacity 0.3s ease',
                '&:hover': { opacity: 0.85 }
              }}
            >
              <Box
                component="img"
                src="/brand/anr-logo.svg"
                alt="ANR Mechanical"
                sx={{ height: { xs: 32, md: 40 }, width: 'auto', display: 'block' }}
              />
            </Box>
          </Box>
          
          {isMobile ? (
            <>
              <IconButton 
                edge="end" 
                onClick={handleToggle}
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  border: 'none'
                }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer 
                anchor="right" 
                open={open} 
                onClose={handleToggle}
                PaperProps={{
                  sx: { width: 300, backgroundColor: 'background.paper' }
                }}
              >
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box
                      component="img"
                      src="/brand/anr-mark.svg"
                      alt="ANR Mechanical"
                      sx={{ height: 36, width: 36, display: 'block' }}
                    />
                    <IconButton onClick={handleToggle} sx={{ color: 'text.primary' }}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  
                  <List sx={{ flexGrow: 1 }}>
                    {navItems.map((item, index) => (
                      <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton 
                          component="a" 
                          href={item.href} 
                          onClick={handleNavClick}
                          sx={{ 
                            borderRadius: 2,
                            py: 1.5,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(11, 31, 59, 0.08)'
                            }
                          }}
                        >
                          <ListItemText 
                            primary={item.label}
                            primaryTypographyProps={{
                              fontWeight: 500,
                              fontSize: '1rem'
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  
                  <Button 
                    fullWidth 
                    variant="contained" 
                    color="secondary" 
                    component="a" 
                    href="tel:(516) 414-0165"
                    sx={{ 
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 600
                    }}
                  >
                    Call Now
                  </Button>
                </Box>
              </Drawer>
            </>
          ) : (
            <NavLinks />
          )}
        </Toolbar>
      </AppBar>
        
        {/* Scroll to Top Button */}
        <Fab
          color="primary"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: theme.zIndex.tooltip,
            opacity: showScrollTop ? 1 : 0,
            transform: showScrollTop ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 0.3s ease',
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)'
            }
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </>
    );
};

export default Header;
